import { useEffect, useState, type ComponentType } from "react";

type DevtoolsPanel = ComponentType<{ initialIsOpen?: boolean }>;

/**
 * React Query devtools, reached only in development.
 *
 * The import is dynamic and sits inside an `import.meta.env.DEV` branch on
 * purpose. v4 already exports a production no-op, so a static import cost
 * nothing visible at runtime — but a bundler still has to RESOLVE it, which made
 * `@tanstack/react-query-devtools` a genuine build-time dependency of the app and
 * forced it into `dependencies`. Vite replaces the flag with `false` in a
 * production build, so the branch is dead code, Rollup drops the `import()` with
 * it, and the package stops being needed to build at all.
 *
 * Same idiom as the sandbox-route guard in `App.tsx`, except that one static-imports
 * local source (which always resolves); a package has to be reached dynamically or
 * the reference survives.
 */
export function DevTools() {
	const [Panel, setPanel] = useState<DevtoolsPanel | null>(null);

	useEffect(() => {
		if (!import.meta.env.DEV) return;

		let cancelled = false;
		import("@tanstack/react-query-devtools").then(({ ReactQueryDevtools }) => {
			// The updater form, or setState would call the component as a reducer.
			if (!cancelled) setPanel(() => ReactQueryDevtools);
		});

		return () => {
			cancelled = true;
		};
	}, []);

	if (!Panel) return null;

	return <Panel initialIsOpen={false} />;
}
